"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { AuthContext } from "@/components/auth/Auth";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface SearchWavesParams {
  readonly author?: string | undefined;
  readonly name?: string | undefined;
  readonly limit: number;
  readonly serial_no_less_than?: number | undefined;
  readonly group_id?: string | undefined;
  readonly direct_message?: boolean | undefined;
}
interface UseWavesParams {
  readonly identity: string | null;
  readonly waveName: string | null;
  readonly limit?: number | undefined;
  readonly refetchInterval?: number | undefined;
  readonly enabled?: boolean | undefined;
  readonly directMessage?: boolean | undefined;
}
export function useWaves(t0) {
  const $ = _c(43);
  const {
    identity,
    waveName,
    limit: t1,
    refetchInterval: t2,
    enabled: t3,
    directMessage
  } = t0;
  const limit = t1 === undefined ? 20 : t1;
  const refetchInterval = t2 === undefined ? Infinity : t2;
  const enabled = t3 === undefined ? true : t3;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const usePublicWaves = !connectedProfile?.handle || !!activeProfileProxy;
  const t4 = identity ?? undefined;
  const t5 = waveName ?? undefined;
  let t6;
  if ($[0] !== directMessage || $[1] !== limit || $[2] !== t4 || $[3] !== t5) {
    t6 = {
      author: t4,
      name: t5,
      limit,
      direct_message: directMessage
    };
    $[0] = directMessage;
    $[1] = limit;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const params = t6;
  const [debouncedParams, setDebouncedParams] = useState(params);
  let t7;
  if ($[5] !== params) {
    t7 = () => setDebouncedParams(params);
    $[5] = params;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== params) {
    t8 = [params];
    $[7] = params;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  useDebounce(t7, 200, t8);
  let t9;
  if ($[9] !== debouncedParams) {
    t9 = [QueryKey.WAVES, debouncedParams];
    $[9] = debouncedParams;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== debouncedParams || $[12] !== limit) {
    t10 = async t11 => {
      const {
        pageParam
      } = t11;
      const queryParams = {};
      queryParams.limit = `${limit}`;
      if (typeof pageParam === "number") {
        queryParams.serial_no_less_than = `${pageParam}`;
      }
      if (debouncedParams.author) {
        queryParams.author = debouncedParams.author;
      }
      if (debouncedParams.name) {
        queryParams.name = debouncedParams.name;
      }
      if (debouncedParams.direct_message !== undefined) {
        queryParams.direct_message = `${debouncedParams.direct_message}`;
      }
      return await commonApiFetch({
        endpoint: "waves",
        params: queryParams
      });
    };
    $[11] = debouncedParams;
    $[12] = limit;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = enabled && !usePublicWaves;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = getDefaultQueryRetry();
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== refetchInterval || $[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t13 = {
      queryKey: t9,
      queryFn: t10,
      initialPageParam: null,
      getNextPageParam: _temp,
      enabled: t11,
      refetchInterval,
      ...t12
    };
    $[15] = refetchInterval;
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const authQuery = useInfiniteQuery(t13);
  let t14;
  let t15;
  if ($[20] !== debouncedParams) {
    t14 = [QueryKey.WAVES_PUBLIC, debouncedParams];
    t15 = async t16 => {
      const {
        pageParam: pageParam_0
      } = t16;
      const queryParams_0 = {};
      if (typeof pageParam_0 === "number") {
        queryParams_0.serial_no_less_than = `${pageParam_0}`;
      }
      if (debouncedParams.author) {
        queryParams_0.author = debouncedParams.author;
      }
      if (debouncedParams.name) {
        queryParams_0.name = debouncedParams.name;
      }
      if (debouncedParams.direct_message !== undefined) {
        queryParams_0.direct_message = `${debouncedParams.direct_message}`;
      }
      return await commonApiFetch({
        endpoint: "waves-public",
        params: queryParams_0
      });
    };
    $[20] = debouncedParams;
    $[21] = t14;
    $[22] = t15;
  } else {
    t14 = $[21];
    t15 = $[22];
  }
  const t16 = enabled && usePublicWaves;
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = getDefaultQueryRetry();
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== refetchInterval || $[25] !== t14 || $[26] !== t15 || $[27] !== t16) {
    t18 = {
      queryKey: t14,
      queryFn: t15,
      initialPageParam: null,
      getNextPageParam: _temp2,
      enabled: t16,
      refetchInterval,
      ...t17
    };
    $[24] = refetchInterval;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const publicQuery = useInfiniteQuery(t18);
  let t19;
  bb0: {
    if (!enabled) {
      let t20;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = [];
        $[29] = t20;
      } else {
        t20 = $[29];
      }
      t19 = t20;
      break bb0;
    }
    if (usePublicWaves) {
      let t20;
      if ($[30] !== publicQuery.data?.pages) {
        t20 = publicQuery.data?.pages.flat() ?? [];
        $[30] = publicQuery.data?.pages;
        $[31] = t20;
      } else {
        t20 = $[31];
      }
      t19 = t20;
      break bb0;
    }
    let t20;
    if ($[32] !== authQuery.data?.pages) {
      t20 = authQuery.data?.pages.flat() ?? [];
      $[32] = authQuery.data?.pages;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    t19 = t20;
  }
  const waves = t19;
  const activeQuery = usePublicWaves ? publicQuery : authQuery;
  let t20;
  if ($[34] !== activeQuery.error || $[35] !== activeQuery.fetchNextPage || $[36] !== activeQuery.hasNextPage || $[37] !== activeQuery.isFetching || $[38] !== activeQuery.isFetchingNextPage || $[39] !== activeQuery.refetch || $[40] !== activeQuery.status || $[41] !== waves) {
    t20 = {
      waves,
      isFetching: activeQuery.isFetching,
      isFetchingNextPage: activeQuery.isFetchingNextPage,
      hasNextPage: activeQuery.hasNextPage,
      fetchNextPage: activeQuery.fetchNextPage,
      status: activeQuery.status,
      error: activeQuery.error,
      refetch: activeQuery.refetch
    };
    $[34] = activeQuery.error;
    $[35] = activeQuery.fetchNextPage;
    $[36] = activeQuery.hasNextPage;
    $[37] = activeQuery.isFetching;
    $[38] = activeQuery.isFetchingNextPage;
    $[39] = activeQuery.refetch;
    $[40] = activeQuery.status;
    $[41] = waves;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  return t20;
}
function _temp2(lastPage_0) {
  return lastPage_0.at(-1)?.serial_no ?? null;
}
function _temp(lastPage) {
  return lastPage.at(-1)?.serial_no ?? null;
}